/**
 * Centralized RC vs CTD pricing logic.
 *
 * Two "makes" with different cost pipelines:
 *
 *   RC make (default historical pipeline)
 *     fabric cost  → RC fabric pricing per yard (already in fabric.price)
 *     labor        → RC_LABOR table (Fused / Half Canvas / Full Canvas)
 *     surcharges   → RC shipping ($35 per garment) + RC bank fee (3% of subtotal)
 *     markup       → 3.0×
 *     lead time    → 8 weeks
 *
 *   CTD make (in-house / MX CMT)
 *     fabric cost  → same MX/RC fabric.price
 *     labor        → CTD_LABOR table (CTD Fused / CTD Half Canvas / …)
 *     surcharges   → none (CTD is in-house, no shipping or bank fees)
 *     markup       → 2.5× (lower because there's no middleman)
 *     lead time    → 3 weeks
 *
 *   RC Fabric · CTD make (hybrid)
 *     Same pricing pipeline as CTD make. The toggle is purely a workflow
 *     indicator — the order ships in-house, no RC factory fees — but it
 *     records that the fabric came from the RC catalog so the back office
 *     can track sourcing. Construction list, markup, lead time and
 *     surcharges all mirror the plain CTD profile.
 *
 *   CTD Fabric · RC make (hybrid mirror)
 *     Same pricing pipeline as RC make. Fabric is sourced from the CTD
 *     in-house catalog but the garment is built at the RC factory, so all
 *     RC fees still apply (3× markup, $35 shipping, 3% bank, 8-wk lead).
 *     Toggle records the fabric sourcing for the back office.
 *
 * Importable from both POS and Quick Order so the two surfaces never drift.
 */

export const RC_LABOR = {
  Fused:         { "2piece": 350, "3piece": 450, jacket: 250, trouser: 130, pant: 130, vest: 100, shirt: 80, overcoat: 500, tuxedo: 500 },
  "Half Canvas": { "2piece": 450, "3piece": 575, jacket: 320, trouser: 130, pant: 130, vest: 125, shirt: 80, overcoat: 500, tuxedo: 500 },
  "Full Canvas": { "2piece": 600, "3piece": 750, jacket: 420, trouser: 130, pant: 130, vest: 150, shirt: 80, overcoat: 500, tuxedo: 500 },
};

export const CTD_LABOR = {
  "CTD Fused":                       { "2piece": 115, "3piece": 155, jacket: 75,  trouser: 40,  pant: 40,  vest: 40,  shirt: 80, overcoat: 300, tuxedo: 300 },
  "CTD Half Canvas":                 { "2piece": 145, "3piece": 185, jacket: 105, trouser: 40,  pant: 40,  vest: 40,  shirt: 80, overcoat: 300, tuxedo: 300 },
  "CTD High-End Half Canvas":        { "2piece": 176, "3piece": 217, jacket: 135, trouser: 41,  pant: 41,  vest: 41,  shirt: 80, overcoat: 300, tuxedo: 300 },
  "CTD Full Canvas":                 { "2piece": 219, "3piece": 263, jacket: 175, trouser: 44,  pant: 44,  vest: 44,  shirt: 80, overcoat: 300, tuxedo: 300 },
  "CTD Machine Half Canvas":         { "2piece": 176, "3piece": 217, jacket: 135, trouser: 41,  pant: 41,  vest: 41,  shirt: 80, overcoat: 300, tuxedo: 300 },
  "CTD 1/3 Handmade Half Canvas":    { "2piece": 239, "3piece": 296, jacket: 182, trouser: 57,  pant: 57,  vest: 57,  shirt: 80, overcoat: 300, tuxedo: 300 },
  "CTD Machine Full Canvas":         { "2piece": 219, "3piece": 263, jacket: 175, trouser: 44,  pant: 44,  vest: 44,  shirt: 80, overcoat: 300, tuxedo: 300 },
  "CTD 1/3 Handmade Full Canvas":    { "2piece": 282, "3piece": 342, jacket: 222, trouser: 60,  pant: 60,  vest: 60,  shirt: 80, overcoat: 300, tuxedo: 300 },
  "CTD 1/2 Handmade Full Canvas":    { "2piece": 390, "3piece": 520, jacket: 260, trouser: 130, pant: 130, vest: 130, shirt: 80, overcoat: 300, tuxedo: 300 },
  "CTD Full Handmade Full Canvas":   { "2piece": 575, "3piece": 705, jacket: 430, trouser: 145, pant: 145, vest: 130, shirt: 80, overcoat: 300, tuxedo: 300 },
};

export const ALL_LABOR = { ...RC_LABOR, ...CTD_LABOR };

export const FABRIC_USAGE = {
  "2piece": 3.5,
  "3piece": 4.2,
  jacket:   2.2,
  trouser:  1.3,
  pant:     1.3,
  vest:     0.7,
  shirt:    2.0,
  overcoat: 4.0,
  tuxedo:   3.5,
};

// Per-make pipeline parameters
export const MAKE_PROFILES = {
  rc: {
    label: "RC Make",
    blurb: "RCMTM factory, full RC pricing pipeline",
    color: "#3b82f6",          // blue-500
    markup: 3.0,
    rcShippingPerGarment: 35,  // flat
    bankFeePct: 0.03,          // 3% of subtotal
    leadWeeks: 8,
    constructionOptions: () => Object.keys(RC_LABOR),
    defaultConstruction: "Half Canvas",
  },
  ctd: {
    label: "CTD Make",
    blurb: "In-house CTD line, no RC fees",
    color: "#14b8a6",          // teal-500
    markup: 2.5,
    rcShippingPerGarment: 0,
    bankFeePct: 0,
    leadWeeks: 3,
    constructionOptions: () => Object.keys(CTD_LABOR),
    defaultConstruction: "CTD Half Canvas",
  },
  rc_fabric_ctd: {
    label: "RC Fabric · CTD Make",
    blurb: "RC-sourced fabric, in-house CTD construction",
    color: "#f59e0b",          // amber-500 (different from RC blue / CTD teal)
    markup: 2.5,
    rcShippingPerGarment: 0,
    bankFeePct: 0,
    leadWeeks: 3,
    constructionOptions: () => Object.keys(CTD_LABOR),
    defaultConstruction: "CTD Half Canvas",
  },
  ctd_fabric_rc: {
    label: "CTD Fabric · RC Make",
    blurb: "CTD-sourced fabric, RCMTM factory construction",
    color: "#a855f7",          // purple-500 (distinct from the other three)
    markup: 3.0,
    rcShippingPerGarment: 35,
    bankFeePct: 0.03,
    leadWeeks: 8,
    constructionOptions: () => Object.keys(RC_LABOR),
    defaultConstruction: "Half Canvas",
  },
};

/** Detect which make a construction type belongs to. */
export function makeFromConstruction(constructionType) {
  if (!constructionType) return "rc";
  return constructionType.startsWith("CTD") ? "ctd" : "rc";
}

/**
 * Calculate the per-garment retail price given a fabric price/yard, the
 * garment type and the construction type. Returns the final price after the
 * make's markup and surcharges.
 */
export function calculateGarmentPrice(fabricPrice, garmentType, constructionType) {
  if (!fabricPrice || !garmentType) return 0;
  const make = makeFromConstruction(constructionType);
  const profile = MAKE_PROFILES[make];
  const labor = ALL_LABOR[constructionType] || ALL_LABOR[profile.defaultConstruction];
  const fabricCost = fabricPrice * (FABRIC_USAGE[garmentType] || 3.5);
  const laborCost = labor[garmentType] || 450;
  const subtotal = fabricCost + laborCost + profile.rcShippingPerGarment;
  const withBankFee = subtotal * (1 + profile.bankFeePct);
  return Math.round(withBankFee * profile.markup);
}

/**
 * Breakdown helper for the price preview UI. Returns each cost component
 * separately so the surface can show "Fabric · Labor · Shipping · Bank fee
 * · Markup" rows like the Price List page does.
 */
export function priceBreakdown(fabricPrice, garmentType, constructionType) {
  const make = makeFromConstruction(constructionType);
  const profile = MAKE_PROFILES[make];
  const labor = ALL_LABOR[constructionType] || ALL_LABOR[profile.defaultConstruction];
  const fabricCost = (fabricPrice || 0) * (FABRIC_USAGE[garmentType] || 3.5);
  const laborCost = labor[garmentType] || 0;
  const shipping = profile.rcShippingPerGarment;
  const beforeBank = fabricCost + laborCost + shipping;
  const bankFee = beforeBank * profile.bankFeePct;
  const total = (beforeBank + bankFee) * profile.markup;
  return {
    make,
    profile,
    fabricCost: Math.round(fabricCost),
    laborCost,
    shipping,
    bankFee: Math.round(bankFee),
    markup: profile.markup,
    leadWeeks: profile.leadWeeks,
    total: Math.round(total),
  };
}
